import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import menuInitialData from "../initialData/menu-page-data";
import API from "../api";
import { SessionService } from "../services/persistService";
import { getDeliveryPriceByCep } from "../services/getAddressByCEP";
import { useParams } from "react-router-dom";
import { initSocket } from "../services/socket_service";

export const MainContext = createContext({});

export const MainProvider = ({ children }) => {
  const [hasInitialized, setHasInitialized] = useState(false);
  const [restaurant, setRestaurant] = useState(menuInitialData.restaurant);
  const [categories, setCategories] = useState(menuInitialData.categories);
  const [restaurantDeliveryPrice, setRestaurantDeliveryPrice] = useState(null);
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [order, setOrder] = useState(null);
  const [posItem, setPosItem] = useState([]);
  const [address, setAddress] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [isOrderFinishedSuccess, setIsOrderFinishedSuccess] = useState(false);
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState("");
  const [currentRestaurantId, setCurrentRestaurantId] = useState(null);

  let { restaurantId, productId } = useParams();

  const fetchData = async () => {
    const restaurantCategoriesResponse =
      await API.findRestaurantMenu(currentRestaurantId);

    if (restaurantCategoriesResponse.status !== 200) return;

    const categories = restaurantCategoriesResponse.data.categories;

    setCategories(categories);
    setRestaurant(restaurantCategoriesResponse.data.restaurant);
    setSettings(restaurantCategoriesResponse.data.restaurant.settings);

    if (productId) {
      let allProducts = categories.flatMap((category) => category.products);

      let product = allProducts.find((product) => product.id === productId);
      setProduct(product);
    }

    const localOrder = SessionService.getItem("order");

    if (localOrder?.items?.length > 0) {
      setOrder(localOrder);
      setShowCart(true);
    }

    const localAddress = SessionService.getItem("address");
    if (localAddress) setAddress(localAddress);
  };

  const findDeliveryPrice = async () => {
    const deliveryPriceFloat = await getDeliveryPriceByCep(
      restaurant.delivery_prices,
    );

    SessionService.addItem(
      "restaurantServesAtAddress",
      deliveryPriceFloat !== null,
    );

    setRestaurantDeliveryPrice(deliveryPriceFloat);
  };

  const clearLocalStorage = () => {
    SessionService.reset(true);
    setCurrentRestaurantId(null);
  };

  const setData = (newId) => {
    if (newId && currentRestaurantId && currentRestaurantId !== newId) {
      clearLocalStorage();
    }

    if (newId && newId.length === 36) {
      SessionService.addItem("restaurantId", newId);
      setCurrentRestaurantId(newId);
    } else {
      setCurrentRestaurantId(SessionService.getItem("restaurantId"));
    }
  };

  useEffect(() => {
    if (order) {
      SessionService.addItem("order", order);
    }
  }, [order]);

  useEffect(() => {
    if (address) {
      SessionService.addItem("address", address);
    }
  }, [address]);

  useEffect(() => {
    setData(restaurantId);
    initSocket();
  }, [restaurantId]);

  useEffect(() => {
    if (currentRestaurantId) {
      fetchData().finally(() => setHasInitialized(true));
    }
  }, [currentRestaurantId]);

  return (
    <MainContext.Provider
      value={{
        search,
        setSearch,
        product,
        setProduct,
        order,
        setOrder,
        products,
        setProducts,
        posItem,
        setPosItem,
        restaurant,
        categories,
        fetchData,
        restaurantDeliveryPrice,
        setRestaurantDeliveryPrice,
        address,
        setAddress,
        showCart,
        setShowCart,
        setIsOrderFinishedSuccess,
        isOrderFinishedSuccess,
        restaurantId: currentRestaurantId,
        clearLocalStorage,
        settings,
        findDeliveryPrice,
        hasInitialized,
      }}
    >
      {children}
    </MainContext.Provider>
  );
};

export const useMainContext = () => useContext(MainContext);
